import { useState, type FormEvent } from 'react';
import { Button } from '@london-life/ui/components/button';
import { Input } from '@london-life/ui/components/input';
import { Label } from '@london-life/ui/components/label';
import { describeError, type FriendlyError } from '@london-life/errors';
import { ErrorNotice } from '@london-life/ui/feedback/error-notice';
import type { ProfileSetup, UsernameAvailability } from '@london-life/contracts';
import { EMPTY_PROFILE, ProfileFields, profileProblem, toProfileSetup, type ProfileValues } from './profile-fields';

type Mode = 'sign-in' | 'sign-up' | 'reset';
type Props = {
  title: string;
  // an information line above the buttons, for example why the visitor was signed out
  notice?: string;
  onGoogle: () => Promise<void>;
  onEmailSignIn: (email: string, password: string) => Promise<void>;
  // creating an account also answers the profile questions, so an under-18 never gets an account at all
  onEmailSignUp?: (email: string, password: string, displayName: string, profile: ProfileSetup) => Promise<void>;
  checkAvailability?: (username: string) => Promise<UsernameAvailability>;
  onReset: (email: string) => Promise<void>;
};

// the same sign-in card for players and admins: Google first, then email and password, then reset
export function SignInCard({
  title,
  notice,
  onGoogle,
  onEmailSignIn,
  onEmailSignUp,
  checkAvailability,
  onReset,
}: Props) {
  const [mode, setMode] = useState<Mode>('sign-in');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [profile, setProfile] = useState<ProfileValues>(EMPTY_PROFILE);
  const [availability, setAvailability] = useState<UsernameAvailability | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const [failure, setFailure] = useState<FriendlyError | null>(null);

  // run one action, show any failure in plain words from the shared catalogue, never leave the form stuck
  const run = async (work: () => Promise<void>, done?: string) => {
    setBusy(true);
    setNote(null);
    setFailure(null);
    try {
      await work();
      if (done) setNote(done);
    } catch (error) {
      setFailure(describeError(error));
    } finally {
      setBusy(false);
    }
  };
  const switchTo = (next: Mode) => {
    setMode(next);
    setFailure(null);
    setNote(null);
  };
  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (mode === 'reset') return void run(() => onReset(email), 'Check your inbox for the reset link.');
    if (mode === 'sign-up' && onEmailSignUp) {
      const problem = profileProblem(profile, availability);
      if (problem) return setFailure({ kind: 'validation', message: problem, retryable: false });
      return void run(() => onEmailSignUp(email, password, name, toProfileSetup(profile)));
    }
    return void run(() => onEmailSignIn(email, password));
  };

  return (
    <div className="sign-in-card">
      <h1>{title}</h1>
      {notice && (
        <p className="sign-in-note" role="status">
          {notice}
        </p>
      )}
      <Button type="button" className="sign-in-google" disabled={busy} onClick={() => void run(onGoogle)}>
        Continue with Google
      </Button>
      <div className="sign-in-divider">
        <span>or use your email</span>
      </div>
      <form onSubmit={submit} className="sign-in-form">
        {mode === 'sign-up' && (
          <div>
            <Label htmlFor="sign-in-name">Your name</Label>
            <Input
              id="sign-in-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              required
              minLength={2}
              autoComplete="name"
            />
          </div>
        )}
        <div>
          <Label htmlFor="sign-in-email">Email</Label>
          <Input
            id="sign-in-email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
            autoComplete="email"
          />
        </div>
        {mode !== 'reset' && (
          <div>
            <Label htmlFor="sign-in-password">Password</Label>
            <Input
              id="sign-in-password"
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
              minLength={8}
              autoComplete={mode === 'sign-up' ? 'new-password' : 'current-password'}
            />
          </div>
        )}
        {mode === 'sign-up' && checkAvailability && (
          <ProfileFields
            values={profile}
            onChange={setProfile}
            checkAvailability={checkAvailability}
            onAvailability={setAvailability}
            idPrefix="sign-up"
          />
        )}
        <Button type="submit" disabled={busy}>
          {mode === 'reset' ? 'Send reset link' : mode === 'sign-up' ? 'Create account' : 'Sign in'}
        </Button>
      </form>
      {failure && (
        <ErrorNotice error={failure}>
          {failure.code === 'auth/email-already-in-use' && (
            <div className="sign-in-links">
              <button type="button" onClick={() => switchTo('sign-in')}>
                Sign in instead
              </button>
              <button type="button" onClick={() => switchTo('reset')}>
                Reset my password
              </button>
            </div>
          )}
        </ErrorNotice>
      )}
      {note && (
        <p className="sign-in-note" role="status">
          {note}
        </p>
      )}
      <div className="sign-in-links">
        {mode !== 'sign-in' && (
          <button type="button" onClick={() => switchTo('sign-in')}>
            Back to sign in
          </button>
        )}
        {mode === 'sign-in' && (
          <button type="button" onClick={() => switchTo('reset')}>
            Forgot password?
          </button>
        )}
        {mode === 'sign-in' && onEmailSignUp && (
          <button type="button" onClick={() => switchTo('sign-up')}>
            Create an account
          </button>
        )}
      </div>
    </div>
  );
}
